<template>
  <div class="fields-filter">
    <button
      v-b-toggle.filter-collapse
      id="filters"
      type="button"
      @click="showTooltip = false"
      :class="`btn ${
        hasAppliedFilters ? 'btn-success' : 'btn-dark'
      } font-weight-bolder font-size-sm mb-2 fields-filter__toggle`"
    >
      <FilterOutline :size="18" />
    </button>

    <b-tooltip target="filters" placement="left" :show.sync="showTooltip">
      Filtros
    </b-tooltip>

    <template v-if="hasAdvancedLayout">
      <b-collapse id="filter-collapse">
        <div class="fields-filter__quick-panel mb-2">
          <div class="fields-filter__quick-grid">
            <div
              v-for="field in quickFields"
              :key="field.field"
              class="fields-filter__quick-item"
              :class="`fields-filter__quick-item--${
                field.quickFilterSize || 'md'
              }`"
            >
              <FilterFieldControl
                compact
                :field="field"
                :value="fieldsValues[field.field]"
                :operator-value="filterOperatorsToAply[field.field]"
                :date-values="dateValues"
                :render-key="keyToRender"
                @input="(value) => setFieldValue(value, field.field)"
                @operator-input="
                  (value) => setFilterOperator(value, field.field)
                "
                @date-input="(value) => setSelectedDate(value, field.field)"
                @enter="aplicarFiltros"
              />
            </div>

            <div class="fields-filter__quick-actions">
              <b-button variant="primary" @click="aplicarFiltros">
                Aplicar
              </b-button>
              <b-button
                variant="outline-primary"
                class="fields-filter__advanced-button"
                @click="openAdvancedFilters"
              >
                <TuneVariant :size="17" class="mr-1" />
                Mais filtros
                <span
                  v-if="advancedAppliedFilterCount"
                  class="fields-filter__count"
                >
                  {{ advancedAppliedFilterCount }}
                </span>
              </b-button>
            </div>
          </div>
        </div>
      </b-collapse>

      <div
        v-if="hasAppliedFilters"
        class="fields-filter__chips mb-2"
        aria-label="Filtros aplicados"
      >
        <div
          v-for="item in appliedFilterItems"
          :key="item.field"
          class="fields-filter__chip"
        >
          <span>{{ item.label }}:</span>
          <strong>{{ item.value }}</strong>
          <button
            type="button"
            class="fields-filter__chip-remove"
            :aria-label="`Remover filtro ${item.label}`"
            @click="removeFilter(item.field)"
          >
            <Close :size="14" />
          </button>
        </div>
        <button
          type="button"
          class="fields-filter__clear-all"
          @click="removerFiltros"
        >
          Limpar todos
        </button>
      </div>

      <b-sidebar
        id="fields-filter-advanced"
        v-model="advancedFiltersVisible"
        title="Filtros avançados"
        width="560px"
        backdrop-variant="dark"
        backdrop
        right
        shadow
        body-class="fields-filter__sidebar-body"
      >
        <div class="fields-filter__advanced-content">
          <section
            v-for="section in advancedSections"
            :key="section.name"
            class="fields-filter__section"
          >
            <h5>{{ section.name }}</h5>
            <div class="fields-filter__advanced-grid">
              <div
                v-for="field in section.fields"
                :key="field.field"
                :class="{
                  'fields-filter__advanced-item--full':
                    field.advancedFilterSize === 'full',
                }"
              >
                <FilterFieldControl
                  compact
                  :field="field"
                  :value="fieldsValues[field.field]"
                  :operator-value="filterOperatorsToAply[field.field]"
                  :date-values="dateValues"
                  :render-key="keyToRender"
                  @input="(value) => setFieldValue(value, field.field)"
                  @operator-input="
                    (value) => setFilterOperator(value, field.field)
                  "
                  @date-input="(value) => setSelectedDate(value, field.field)"
                  @enter="aplicarFiltros"
                />
              </div>
            </div>
          </section>
        </div>

        <template #footer>
          <div class="fields-filter__sidebar-footer">
            <b-button variant="primary" @click="aplicarFiltros">
              Aplicar filtros
            </b-button>
            <b-button variant="outline-primary" @click="clearAdvancedFilters">
              Limpar
            </b-button>
          </div>
        </template>
      </b-sidebar>
    </template>

    <template v-else>
      <div
        v-if="hasAppliedFilters"
        class="fields-filter__summary bg-white my-2 d-flex justify-content-between rounded align-items-center"
      >
        <p class="mb-0 font-weight-bold">
          <span class="text-primary mx-2">Filtros:</span>
          {{ appliedFiltersText }}
        </p>
        <b-button
          variant="primary"
          class="font-size-sm ml-3 font-weight-bolder rounded-right"
          @click="removerFiltros"
        >
          Limpar filtros
        </b-button>
      </div>

      <b-collapse id="filter-collapse">
        <b-card class="mb-2">
          <b-row>
            <b-col
              v-for="field in fields"
              :key="field.field"
              :md="field.filterOperators ? 4 : 3"
            >
              <FilterFieldControl
                :field="field"
                :value="fieldsValues[field.field]"
                :operator-value="filterOperatorsToAply[field.field]"
                :date-values="dateValues"
                :render-key="keyToRender"
                @input="(value) => setFieldValue(value, field.field)"
                @operator-input="
                  (value) => setFilterOperator(value, field.field)
                "
                @date-input="(value) => setSelectedDate(value, field.field)"
                @enter="aplicarFiltros"
              />
            </b-col>
          </b-row>
          <b-row class="mt-2">
            <b-col>
              <b-button variant="primary" @click="aplicarFiltros" class="mr-2">
                Aplicar filtros
              </b-button>
              <b-button variant="secondary" @click="removerFiltros">
                Remover filtros
              </b-button>
            </b-col>
          </b-row>
        </b-card>
      </b-collapse>
    </template>
  </div>
</template>

<script>
import Close from "vue-material-design-icons/Close";
import FilterOutline from "vue-material-design-icons/Filter";
import TuneVariant from "vue-material-design-icons/TuneVariant";
import { filtersStore } from "./filtersStore";
import FilterFieldControl from "./FilterFieldControl.vue";
import moment from "moment";

export default {
  name: "fields-filter",
  components: {
    Close,
    FilterFieldControl,
    FilterOutline,
    TuneVariant,
  },
  props: ["fields", "showComponent"],
  data() {
    return {
      advancedFiltersVisible: false,
      appliedFilterItems: [],
      dateValues: {},
      fieldsValues: this.fields.reduce(
        (values, field) => ({
          ...values,
          [field.field]: null,
        }),
        {}
      ),
      filterOperatorsToAply: this.getDefaultFilterOperators(),
      filtersStore,
      filtersToAply: {},
      keyToRender: 0,
      showTooltip: false,
    };
  },
  watch: {
    fields: {
      handler() {
        this.appliedFilterItems = this.buildAppliedFilterItems();
      },
      deep: true,
    },
    filtersStore: {
      handler(store) {
        if (!store[this.moduleName]) return;

        this.filtersToAply = { ...(store[this.moduleName].filters || {}) };
        this.filterOperatorsToAply = {
          ...this.getDefaultFilterOperators(),
          ...(store[this.moduleName].filterOperators || {}),
        };
        this.dateValues = {};

        this.fields.forEach((field) => {
          this.fieldsValues[field.field] =
            this.filtersToAply[field.field] ?? null;

          const rangeField = `range_${field.field}`;
          if (this.filtersToAply[rangeField]?.length) {
            this.dateValues = {
              ...this.dateValues,
              [rangeField]: this.filtersToAply[rangeField].join(" até "),
            };
          }
        });

        this.appliedFilterItems = this.buildAppliedFilterItems();
      },
      deep: true,
      immediate: true,
    },
  },
  methods: {
    aplicarFiltros() {
      if (!this.filtersStore[this.moduleName]) return;

      this.$set(this.filtersStore, this.moduleName, {
        ...this.filtersStore[this.moduleName],
        filters: { ...this.filtersToAply },
        filterOperators: { ...this.filterOperatorsToAply },
        page: 1,
      });
      this.advancedFiltersVisible = false;
    },
    buildAppliedFilterItems() {
      return this.fields
        .filter((field) => {
          const value = this.filtersToAply[field.field];
          const rangeValue = this.filtersToAply[`range_${field.field}`];
          return this.hasFilterValue(value) || rangeValue?.length;
        })
        .map((field) => ({
          field: field.field,
          isAdvanced: !field.quickFilter,
          label: this.getAppliedFilterLabel(field),
          value: this.getAppliedFilterValue(field),
        }));
    },
    clearAdvancedFilters() {
      this.advancedFields.forEach((field) => {
        this.removeLocalFilter(field.field);
      });
      this.aplicarFiltros();
    },
    getAppliedFilterLabel(field) {
      const operatorLabel = this.getFilterOperatorLabel(field);
      return operatorLabel ? `${field.label} ${operatorLabel}` : field.label;
    },
    getAppliedFilterValue(field) {
      const value = this.filtersToAply[field.field];
      const rangeValue = this.filtersToAply[`range_${field.field}`];
      if (field.type === "select" && this.hasFilterValue(value)) {
        const option = field.options?.find(
          (item) =>
            item &&
            typeof item === "object" &&
            String(item.value) === String(value)
        );
        if (option) return option.text;
      }
      return this.hasFilterValue(value)
        ? value
        : this.handleLabelDates(rangeValue);
    },
    getDefaultFilterOperators() {
      return this.fields.reduce((operators, field) => {
        if (!field.filterOperators?.length) return operators;

        return {
          ...operators,
          [field.field]:
            field.defaultFilterOperator ?? field.filterOperators[0].value,
        };
      }, {});
    },
    getFilterOperatorLabel(field) {
      if (!field.filterOperators?.length) return null;

      const selectedValue = this.filterOperatorsToAply[field.field];
      const operator = field.filterOperators.find(
        (item) => item.value === selectedValue
      );
      return operator?.summaryText || operator?.text?.toLowerCase();
    },
    handleLabelDates(arrayDates) {
      if (!arrayDates?.length) return "";
      if (arrayDates.length > 1) {
        return `${moment(arrayDates[0]).format("DD/MM/YYYY")} até ${moment(
          arrayDates[arrayDates.length - 1]
        ).format("DD/MM/YYYY")}`;
      }
      return moment(arrayDates[0]).format("DD/MM/YYYY");
    },
    hasFilterValue(value) {
      return value !== null && value !== undefined && value !== "";
    },
    openAdvancedFilters() {
      this.$root.$emit("bv::toggle::collapse", "fields-filter-advanced");
    },
    removeFilter(fieldName) {
      this.removeLocalFilter(fieldName);
      this.aplicarFiltros();
    },
    removeLocalFilter(fieldName) {
      const filters = { ...this.filtersToAply };
      delete filters[fieldName];
      delete filters[`range_${fieldName}`];
      this.filtersToAply = filters;
      this.fieldsValues[fieldName] = null;
      this.$delete(this.dateValues, `range_${fieldName}`);
    },
    removerFiltros() {
      if (!this.filtersStore[this.moduleName]) return;

      this.filtersToAply = {};
      this.filterOperatorsToAply = this.getDefaultFilterOperators();
      this.fields.forEach((field) => {
        this.fieldsValues[field.field] = null;
      });
      this.dateValues = {};
      this.keyToRender += 1;

      this.$set(this.filtersStore, this.moduleName, {
        ...this.filtersStore[this.moduleName],
        filters: {},
        filterOperators: {},
        page: 1,
      });
    },
    setFieldValue(value, field) {
      this.fieldsValues[field] = value;
      this.filtersToAply = {
        ...this.filtersToAply,
        [field]: value,
      };
    },
    setFilterOperator(value, field) {
      this.filterOperatorsToAply = {
        ...this.filterOperatorsToAply,
        [field]: value,
      };
    },
    setSelectedDate(value, field) {
      const arrayOfDates = value.split(" até ");
      this.filtersToAply = {
        ...this.filtersToAply,
        [`range_${field}`]: arrayOfDates,
      };
      this.dateValues = {
        ...this.dateValues,
        [`range_${field}`]: value,
      };
    },
  },
  computed: {
    advancedAppliedFilterCount() {
      return this.appliedFilterItems.filter((item) => item.isAdvanced).length;
    },
    advancedFields() {
      return this.fields.filter((field) => !field.quickFilter);
    },
    advancedSections() {
      return this.advancedFields.reduce((sections, field) => {
        const sectionName = field.filterSection || "Outros";
        let section = sections.find((item) => item.name === sectionName);
        if (!section) {
          section = { name: sectionName, fields: [] };
          sections.push(section);
        }
        section.fields.push(field);
        return sections;
      }, []);
    },
    appliedFiltersText() {
      return this.appliedFilterItems
        .map((item) => `${item.label}: ${item.value}`)
        .join(", ");
    },
    hasAdvancedLayout() {
      return this.fields.some((field) => field.quickFilter);
    },
    hasAppliedFilters() {
      return this.appliedFilterItems.length > 0;
    },
    moduleName() {
      return this.$route.fullPath.replace("/", "");
    },
    quickFields() {
      return this.fields.filter((field) => field.quickFilter);
    },
  },
};
</script>

<style>
.fields-filter {
  display: flex;
  flex-direction: column;
}

.fields-filter__toggle {
  max-width: 40px;
}

.fields-filter__summary {
  min-height: 38px;
}

.fields-filter__quick-panel {
  background: #ffffff;
  border: 1px solid #edf0f5;
  border-radius: 6px;
  padding: 18px;
}

.fields-filter__quick-grid {
  align-items: flex-end;
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
}

.fields-filter__quick-item {
  min-width: 150px;
}

.fields-filter__quick-item--sm {
  flex: 1 1 150px;
}

.fields-filter__quick-item--md {
  flex: 1.3 1 190px;
}

.fields-filter__quick-item--lg {
  flex: 1.8 1 260px;
}

.fields-filter__quick-item--xl {
  flex: 2.4 1 360px;
}

.fields-filter__quick-actions {
  align-items: center;
  display: flex;
  flex: 0 0 auto;
  gap: 10px;
}

.fields-filter__quick-actions .btn {
  height: 38px;
  white-space: nowrap;
}

.fields-filter__advanced-button {
  align-items: center;
  display: inline-flex;
}

.fields-filter__count {
  align-items: center;
  background: var(--primary-color);
  border-radius: 50%;
  color: #ffffff;
  display: inline-flex;
  font-size: 11px;
  font-weight: 700;
  height: 20px;
  justify-content: center;
  margin-left: 6px;
  min-width: 20px;
  padding: 0 5px;
}

.fields-filter__chips {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.fields-filter__chip {
  align-items: center;
  background: rgba(246, 78, 96, 0.08);
  border: 1px solid rgba(246, 78, 96, 0.18);
  border-radius: 5px;
  color: #4b5068;
  display: inline-flex;
  font-size: 12px;
  gap: 4px;
  min-height: 30px;
  padding: 4px 6px 4px 9px;
}

.fields-filter__chip strong {
  color: var(--primary-color);
  font-weight: 700;
}

.fields-filter__chip-remove {
  align-items: center;
  background: transparent;
  border: 0;
  color: var(--primary-color);
  display: inline-flex;
  height: 22px;
  justify-content: center;
  padding: 0;
  width: 22px;
}

.fields-filter__clear-all {
  background: transparent;
  border: 0;
  color: var(--primary-color);
  font-size: 12px;
  font-weight: 700;
  min-height: 30px;
  padding: 4px 8px;
}

.fields-filter__advanced-content {
  padding: 4px 20px 20px;
}

.fields-filter__section {
  border-bottom: 1px solid #edf0f5;
  padding: 18px 0;
}

.fields-filter__section:last-child {
  border-bottom: 0;
}

.fields-filter__section h5 {
  color: #181c32;
  font-size: 13px;
  font-weight: 700;
  margin: 0 0 14px;
}

.fields-filter__advanced-grid {
  display: grid;
  gap: 14px 12px;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
}

.fields-filter__advanced-item--full {
  grid-column: 1 / -1;
}

.fields-filter__sidebar-body {
  overflow-y: auto;
}

.fields-filter__sidebar-footer {
  background: #ffffff;
  border-top: 1px solid #edf0f5;
  display: flex;
  gap: 10px;
  padding: 14px 20px;
}

.fields-filter__sidebar-footer .btn {
  min-width: 120px;
}

.filter-fields-container {
  margin-bottom: 30px;
}

@media (max-width: 991px) {
  .fields-filter__quick-actions {
    flex: 1 1 100%;
  }
}

@media (max-width: 575px) {
  #fields-filter-advanced {
    width: 100% !important;
  }

  .fields-filter__advanced-grid {
    grid-template-columns: minmax(0, 1fr);
  }

  .fields-filter__advanced-item--full {
    grid-column: auto;
  }

  .fields-filter__quick-item {
    flex-basis: 100%;
  }

  .fields-filter__quick-actions {
    align-items: stretch;
    flex-direction: column;
  }

  .fields-filter__quick-actions .btn {
    width: 100%;
  }
}
</style>
